import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { AppLogo } from '@/components';
import { Button } from '@/components/ui/Button';
import { EngineService } from '@/services/engine/engine-service';
import { useAuth } from '@/hooks/useAuth';
import { useNavigate, useQueryParams } from '@/lib/router-adapter';
import { AppRoutes } from '@/constants/routes';
import { I18nNamespace } from '@/lib/i18n-namespaces';
import { useDocumentTitle } from '@/hooks';
import { RETURN_TO_PARAM, toSafeReturnTo } from '@/lib/sign-in-redirect';
import '@/styles/conntour.css';

const AUTH_FAILED_ERROR = 'auth_failed';

export default function SignInPage() {
    useDocumentTitle('signIn');
    const { t } = useTranslation(I18nNamespace.AUTH);
    const navigate = useNavigate();
    const { isAuthenticated, isLoading: authLoading } = useAuth();
    const queryParams = useQueryParams();

    const hasFailed = queryParams.get('error') === AUTH_FAILED_ERROR;
    const returnTo = toSafeReturnTo(queryParams.get(RETURN_TO_PARAM));

    useEffect(() => {
        if (authLoading) {
            return;
        }

        if (isAuthenticated) {
            navigate.replace(returnTo ?? AppRoutes.HOME);
            return;
        }

        if (!hasFailed) {
            EngineService.initiateLogin(returnTo);
        }
    }, [hasFailed, returnTo, authLoading, isAuthenticated, navigate]);

    if (!hasFailed) {
        return <AppLogo />;
    }

    return (
        <div className="min-h-screen flex flex-col items-center justify-center gap-6 px-4 contour-bg">
            <div className="text-center">
                <h1 className="text-xl font-bold text-white/90 mb-2">
                    {t('signIn.errors.authFailed')}
                </h1>
                <p className="text-sm text-white/70">
                    {t('signIn.errors.authFailedMessage')}
                </p>
            </div>
            <Button
                variant="outline"
                onClick={() => EngineService.initiateLogin(returnTo)}
            >
                {t('signIn.retryButton')}
            </Button>
        </div>
    );
}
